import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { apiFetch } from './utils/api';
import './App.css';
import { loginRequest } from './authConfig';
import OrganizationSelector from './pages/OrganizationSelector';
import OrganizationDashboard from './pages/OrganizationDashboard';
import EmployeeManagement from './pages/EmployeeManagement';
import PayrollPeriods from './pages/PayrollPeriods';
import PayrollRecords from './pages/PayrollRecords';
import PayrollReports from './pages/PayrollReports';
import AdminPage from './pages/AdminPage';
import DBManagement from './pages/DBManagement';
import Students from './components/Students';
import Support from './pages/Support';
import BulkExport from './components/BulkExport';
import SplashCursor from './components/SplashCursor';
import UnauthorizedPage from './pages/UnauthorizedPage';
import Layout from './components/Layout';

// ---------------------------------------------------------------------------
// SignInScreen
// Full-bleed radial-glow background with a centered sign-in card. The card
// is split into a white top section (large logo) and a dark bottom section
// (title/subtitle, feature pills, sign-in button, footnote). Styles live in
// App.css (.signin, .signin__*, .ms-signin-btn* rules).
// ---------------------------------------------------------------------------

function SignInScreen({ onSignIn }) {
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    setLoading(true);
    onSignIn();
  };

  return (
    <div className="signin">
      <SplashCursor COLOR="#2f6fed" RAINBOW_MODE={false} SPLAT_FORCE={4500} DENSITY_DISSIPATION={3.5} />

      <div className="signin__panel">
        <div className="signin__content">
          <div className="signin__content-top">
            <div className="signin__logo-row">
              <img src="/op_logo.png" alt="Oriental Business Solutions" className="signin__logo" />
            </div>
          </div>

          <div className="signin__content-bottom">
            <p className="signin__eyebrow">Payroll Platform</p>
            <h2 className="signin__title">OrientalPay</h2>
            <p className="signin__subtitle">
              Sign in with your Microsoft account to manage employees, payroll periods, and reports.
            </p>
            <div className="signin__features">
              <span className="signin__feature">Employee Management</span>
              <span className="signin__feature">Payroll Periods</span>
              <span className="signin__feature">Payroll Records</span>
              <span className="signin__feature">Reports &amp; Bulk Export</span>
            </div>

            <button className="ms-signin-btn" onClick={handleClick} disabled={loading}>
              {loading ? (
                <span className="ms-signin-btn__spinner" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
              )}
              {loading ? 'Signing in…' : 'Sign in with email'}
            </button>

            <p className="signin__footnote">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              Secured by your organization&apos;s Microsoft 365 sign-in
            </p>
          </div>
        </div>

        <div className="signin__mockup">
          <img
            src="/laptop.png"
            alt="Payroll dashboard shown on a laptop and phone"
            className="signin__mockup-img"
          />
        </div>
      </div>
    </div>
  );
}

function App() {
  const { instance, inProgress } = useMsal();
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (inProgress === 'none' && !isInitialized) {
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount && instance.getAllAccounts().length > 0) {
        instance.setActiveAccount(instance.getAllAccounts()[0]);
      }

      const fetchUserRole = async () => {
        const account = instance.getActiveAccount();
        if (!account) {
          setIsInitialized(true);
          return;
        }

        try {
          const data = await apiFetch(`/user-role?email=${account.username}`);
          setUser(data);
          setUserRole(data.role);
          setTenantId(data.tenant_identifier);

          if (data.role === 'l1' || data.role === 'l2') {
            const org = { id: data.organization_id, name: data.organization_name };
            if (data.organization_id && data.tenant_identifier) {
              const fullOrg = await apiFetch(`/organizations/${data.organization_id}`, data.tenant_identifier);
              const orgWithTenant = { ...fullOrg, tenantId: data.tenant_identifier };
              setSelectedOrg(orgWithTenant);
              localStorage.setItem('selectedOrg', JSON.stringify(orgWithTenant));
            }
          } else {
            const storedOrg = localStorage.getItem('selectedOrg');
            if (storedOrg) {
              setSelectedOrg(JSON.parse(storedOrg));
            }
          }
        } catch (error) {
          console.error("Failed to fetch user role, defaulting to L3:", error);
          setUserRole('norole'); // Default on error to show unauthorized page
        } finally {
          setIsInitialized(true);
        }
        if (account.username === 'admin@orientalpay.ca') {
          setUserRole('admin');
        }
      };

      fetchUserRole();
    } // eslint-disable-next-line
  }, [inProgress, instance, isInitialized]);

  // This effect runs on every navigation change to sync localStorage with component state.
  // This is crucial for the admin "view as tenant" feature.
  useEffect(() => {
    const storedOrg = localStorage.getItem('selectedOrg');
    if (storedOrg) {
      const parsedOrg = JSON.parse(storedOrg);
      if (JSON.stringify(parsedOrg) !== JSON.stringify(selectedOrg)) {
        setSelectedOrg(parsedOrg);
      }
    }
  }, [location, selectedOrg]);

  // When an admin "views as" a tenant, the selectedOrg changes.
  // This effect ensures the App's root tenantId state is also updated.
  useEffect(() => {
    if (userRole === 'admin' && selectedOrg?.tenantId && selectedOrg.tenantId !== tenantId) {
      setTenantId(selectedOrg.tenantId);
    }
  }, [selectedOrg, userRole, tenantId]);
  const handleSelectOrg = (org) => {
    const orgWithTenantId = { ...org, tenantId: org.tenantId || tenantId };
    setSelectedOrg(orgWithTenantId);
    localStorage.setItem('selectedOrg', JSON.stringify(orgWithTenantId));
  };

  const handleCreateOrg = async (orgData) => {
    try {
      const newOrg = await apiFetch('/organizations', tenantId, {
        method: 'POST',
        body: orgData,
      });
      alert('Organization created successfully!');
      return newOrg;
    } catch (error) {
      console.error('Error creating organization:', error);
      alert('Failed to create organization');
    }
  };

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(e => {
      console.error(e);
    });
  };

  if (!isInitialized) {
    return <div className="signin" />; // Render a blank loading screen
  }

  return (
    
      <div className="App">
        {userRole === 'norole' && (
          <AuthenticatedTemplate><UnauthorizedPage /></AuthenticatedTemplate>
        )}
        {userRole !== 'norole' && (
        <AuthenticatedTemplate>
          <Layout selectedOrg={selectedOrg} onSelectOrg={handleSelectOrg} userRole={userRole}>
            <Routes>
              <Route path="/" element={<Navigate to={userRole === 'admin' ? '/admin' : (userRole === 'l3' && !selectedOrg ? "/org-management" : "/dashboard")} replace />} />
              <Route path="/landing" element={<Navigate to={userRole === 'admin' ? '/admin' : (userRole === 'l3' && !selectedOrg ? "/org-management" : "/dashboard")} replace />} />

              <Route
                path="/org-management"
                element={userRole === 'l3' || userRole === 'admin' ? (
                    <OrganizationSelector
                      selectedOrg={selectedOrg} // selectedOrg will have the tenantId property
                      onSelectOrg={handleSelectOrg}
                      tenantId={selectedOrg?.tenantId || tenantId} // Use selectedOrg's tenantId if available, else admin's tenantId
                      onCreateOrg={handleCreateOrg}
                    />
                  ) : <Navigate to="/dashboard" replace />
                }
              />
              {selectedOrg && (
                <>
                  <Route path="/dashboard" element={<OrganizationDashboard org={selectedOrg} userRole={userRole} onOrgUpdated={handleSelectOrg} />} />
                  <Route path="/employees" element={<EmployeeManagement org={selectedOrg} userRole={userRole} instance={instance} />} />
                  <Route path="/payroll-periods" element={<PayrollPeriods org={selectedOrg} user={user} />} />
                  <Route path="/payroll-records" element={<PayrollRecords org={selectedOrg} user={user} />} />
                  <Route path="/payroll-reports" element={<PayrollReports org={selectedOrg} />} />
                  <Route path="/support" element={<Support org={selectedOrg} />} />
                  <Route path="/students" element={<Students org={selectedOrg} />} />
                  <Route path="/bulk-export" element={<BulkExport org={selectedOrg} />} />
                </>
              )}
              {userRole === 'admin' && (
                <>
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/admin/db-management" element={<DBManagement />} />
                </>
              )}
            </Routes>
          </Layout>
        </AuthenticatedTemplate>
        )}

        <UnauthenticatedTemplate>
          <SignInScreen onSignIn={handleLogin} />
        </UnauthenticatedTemplate>
      </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWrapper;